import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

import {
  fetchEntitlement,
  redeemOrgLicense,
  type EntitlementResponse,
  type EntitlementStatus,
} from '@/lib/api'
import {
  clearCachedEntitlement,
  loadCachedEntitlement,
  saveCachedEntitlement,
} from '@/lib/entitlement-cache'
import {
  getProducts,
  initIap,
  onEntitlementVerified,
  purchase as iapPurchase,
  restorePurchases as iapRestore,
  type IapProductInfo,
  type ProductId,
  IapNotAvailableError,
  IapPurchaseCancelledError,
} from '@/lib/iap'
import { isVppEdition } from '@/lib/edition'
import { isAndroid, isIOS, isNative } from '@/lib/platform'
import {
  trackPaywallClosed,
  trackPaywallOpened,
  trackPurchaseCompleted,
  trackPurchaseFailed,
  trackPurchaseStarted,
  trackRestoreCompleted,
  trackRestoreFailed,
  trackRestoreStarted,
  trackEntitlementGrace,
  trackEntitlementReconciled,
  trackTrialExpiredDetected,
  type PaywallSource,
  type PurchasePlatform,
  type PurchasePricing,
  type PurchaseProduct,
} from '@/lib/telemetry-events'

/**
 * Single source of truth for "does the current patient have access?".
 *
 * The server is authoritative — every successful refresh is mirrored
 * into a {@link saveCachedEntitlement} write — but the store now also
 * hydrates from that on-disk cache at app boot so a cold launch with
 * no network renders the correct trial / unlocked state immediately
 * instead of sitting on `status: 'unknown'` until the network resolves.
 *
 * Offline trust model (see `lib/entitlement-cache.ts` for the full
 * rationale):
 *   - non-trial statuses: cache trusted indefinitely.
 *   - trial with `trial.ends_at` in the future: cache trusted.
 *   - trial whose cached `ends_at` has elapsed: {@link effectiveStatus}
 *     rolls it to `lapsed`, the red bar shows, and every action routes
 *     to the paywall. This is the one place where offline use is
 *     blocked: once we know the trial should have ended, the patient
 *     has to come online (to restore a purchase or buy).
 *
 * Device-clock manipulation is an accepted threat — a determined user
 * can roll their clock back to keep the cached trial alive forever.
 * The product directive prioritises offline usability for legitimate
 * patients with intermittent connectivity over the rare malicious
 * trial-extender.
 *
 * None of the above applies to the VPP edition, which is unlocked by an
 * institution's license rather than by a purchase. There `effectiveStatus`
 * is pinned to `'vpp'` regardless of cache, network or clock: the server
 * reports no expiry, there is no paywall to fall back to, and access ends
 * when MDM removes the app. See `lib/edition.ts`.
 */
/** How long to wait for a replayed transaction to be verified, in ms. */
const RECONCILE_BUDGET_MS = 3000

export const useEntitlementStore = defineStore('entitlement', () => {
  const status = ref<EntitlementStatus | 'unknown'>('unknown')
  const trialDaysRemaining = ref<number | null>(null)
  const trialEndsAt = ref<string | null>(null)
  const expiresAt = ref<string | null>(null)
  const productId = ref<string | null>(null)
  const platform = ref<'ios' | 'android' | null>(null)

  /** The facility this device is licensed through, or null. Stays set once a
   *  license lapses, so settings can say which facility it was. */
  const organizationName = ref<string | null>(null)

  const loading = ref(false)
  const purchasing = ref(false)
  const lastError = ref<string | null>(null)

  /** True iff the current store contents came from the on-disk cache and
   *  haven't been confirmed by a successful refresh in this session yet.
   *  Reset by every successful `refresh()`; exposed so the UI can render
   *  a subtle "values may be stale" indicator if it wants to. */
  const usingCache = ref(false)

  /** Whether the paywall dialog is currently visible. App.vue mounts a
   *  single <PurchaseDialog/> and binds its v-model:show to this. */
  const paywallOpen = ref(false)

  /**
   * True while {@link reconcile} is checking a state that would otherwise
   * interrupt the patient.
   *
   * Deliberately NOT exported, and nothing in the UI may read it. An earlier
   * version gated the red bar on it, which put a render decision behind an
   * in-flight network call and broke this store's own contract — "the cache is
   * the source of truth for the first paint, and refreshes never block the UI"
   * (see hydrate). What the screens read is `isExpired`, always, immediately;
   * a reconcile that finds a renewal simply updates it in the background. The
   * false-lapse it used to paper over is fixed where it belongs, in
   * EntitlementResolver's subscription grace.
   */
  const reconciling = ref(false)

  /** One attempt per app run — see {@link reconcile}. */
  let reconcileAttempted = false
  let reconcileInflight: Promise<void> | null = null

  /** Shared so concurrent `refresh()` callers await the same request. */
  let refreshInflight: Promise<void> | null = null

  /** One grace signal per app run, however many reads report it. */
  let graceReported = false

  /** Resolves {@link waitForVerification} when a transaction is verified. */
  let verificationSignal: (() => void) | null = null

  /**
   * The status the rest of the app should react to.
   *
   * Identical to the raw `status` for every value except `'trial'`: if
   * the cached `trialEndsAt` has elapsed against the device clock, the
   * trial is reported as `'lapsed'` so all the existing `isExpired`
   * UX (red bar, paywall-on-tap) kicks in without needing every screen
   * to learn about the cache layer.
   */
  const effectiveStatus = computed<EntitlementStatus | 'unknown'>(() => {
    // The VPP edition is unlocked by license, not by purchase. Short-circuit
    // before anything else so a stale cache, a failed refresh or a device
    // clock can never take a licensed device offline: on this edition the
    // server has no expiry to report and there is no paywall to fall back to.
    if (isVppEdition) return 'vpp'
    if (status.value !== 'trial') return status.value
    const endsAt = trialEndsAt.value
    if (endsAt === null) return status.value
    const ends = Date.parse(endsAt)
    if (Number.isNaN(ends)) return status.value
    return ends <= Date.now() ? 'lapsed' : 'trial'
  })

  const isUnlocked = computed(
    () =>
      effectiveStatus.value === 'trial' ||
      effectiveStatus.value === 'subscription' ||
      effectiveStatus.value === 'lifetime' ||
      effectiveStatus.value === 'vpp' ||
      effectiveStatus.value === 'org',
  )

  /** True while a facility license is what's granting access. */
  const isOrgLicensed = computed(() => effectiveStatus.value === 'org')
  const isTrial = computed(() => effectiveStatus.value === 'trial')
  const isExpired = computed(
    () => effectiveStatus.value === 'lapsed' || effectiveStatus.value === 'none',
  )

  /**
   * Report once per app run that grace is what is granting access.
   *
   * Fires from {@link applyResponse}, so a cached payload counts too — which is
   * correct: "this launch relied on grace" is true whether the answer came off
   * disk or the wire.
   */
  function reportGrace(r: EntitlementResponse): void {
    const grace = r.entitlement.grace
    if (!grace || graceReported) return
    graceReported = true
    trackEntitlementGrace(grace)
  }

  function applyResponse(r: EntitlementResponse): void {
    status.value = r.entitlement.status
    trialDaysRemaining.value = r.trial.days_remaining
    trialEndsAt.value = r.trial.ends_at
    expiresAt.value = r.entitlement.expires_at
    productId.value = r.entitlement.product_id
    platform.value = r.entitlement.platform
    organizationName.value = r.entitlement.organization_name ?? null
    reportGrace(r)
  }

  /**
   * Redeem a facility's volume-license token — from the settings dialog, or
   * from the MDM managed configuration at launch.
   *
   * The response is the authoritative entitlement, so it lands in the store
   * and the cache exactly like a refresh would. Errors propagate: the dialog
   * shows the server's message, and the launch-time sync swallows them.
   */
  async function redeemOrgToken(token: string): Promise<void> {
    const response = await redeemOrgLicense(token)
    applyResponse(response)
    usingCache.value = false
    lastError.value = null
    await saveCachedEntitlement(response)
  }

  /**
   * Adopt an entitlement payload that arrived on the back of some other
   * operation, rather than from an entitlement read of our own.
   *
   * One caller: a completed device transfer, whose response carries the
   * surviving account's entitlement so the paywall is correct on the first
   * frame after the dialog closes instead of after a round-trip. Overwrites
   * the on-disk cache in the same breath, because the cached copy describes
   * the account this device has just stopped being — leaving it to be
   * corrected by a later refresh would mean a window where a cold launch
   * reads somebody else's trial clock.
   */
  async function adoptEntitlement(response: EntitlementResponse): Promise<void> {
    applyResponse(response)
    usingCache.value = false
    lastError.value = null
    await saveCachedEntitlement(response)
  }

  /**
   * Pre-populate the store from the on-disk cache (if any), then fire a
   * background refresh. Safe to call repeatedly — App.vue calls it once
   * at mount, LaunchTrialPatient calls it on every entry. Concurrent
   * refreshes fan in via the {@link loading} guard.
   *
   * Unlike {@link refresh}, this method never awaits the network: the
   * cache is the source of truth for the first paint, and refreshes
   * never block the UI.
   */
  async function hydrate(): Promise<void> {
    if (status.value === 'unknown') {
      const cached = await loadCachedEntitlement()
      if (cached !== null) {
        applyResponse(cached.response)
        usingCache.value = true
      }
    }
    // If the cached trial has elapsed by the device clock, log the moment
    // we noticed — drives the trial-expiry → paywall funnel without
    // waiting on the server refresh below to confirm.
    maybeEmitTrialExpired()
    // Fire-and-forget. A failure leaves `usingCache` true and the cached
    // state in place; `effectiveStatus` still does the right thing once
    // the trial window has elapsed.
    void refresh()

    // If what we just loaded would block the patient, start double-checking it
    // now — before the home screen paints, so the red bar never appears and
    // then vanishes. No-op for a trial, a paid entitlement, or an unknown
    // state; see reconcile() for why that matters.
    void reconcile()
  }

  /**
   * Pull current entitlement from the server. Safe to call repeatedly.
   * On success the response is mirrored into the on-disk cache so the
   * next cold launch has something to render before the network
   * resolves. On failure (offline / 5xx) we leave whatever the cache
   * loaded in place — `effectiveStatus` rolls trial→lapsed once the
   * cached window has elapsed, which is the only place offline use is
   * forced to come online.
   */
  async function refresh(): Promise<void> {
    // Fan concurrent callers into the in-flight request rather than returning
    // early. `await refresh()` has to mean "the state is now as fresh as the
    // network allows" — returning immediately because somebody else's refresh
    // was running made it mean "maybe", which is no use to a caller about to
    // decide whether to block the patient.
    if (refreshInflight !== null) return refreshInflight

    loading.value = true
    refreshInflight = (async () => {
      try {
        const response = await fetchEntitlement()
        applyResponse(response)
        usingCache.value = false
        lastError.value = null
        // Persisted after state update — the in-memory store and the
        // cache always agree at the end of a successful refresh.
        await saveCachedEntitlement(response)
      } catch (e) {
        lastError.value = e instanceof Error ? e.message : String(e)
      } finally {
        loading.value = false
        refreshInflight = null
      }
    })()

    await refreshInflight

    // A cold launch with no cache starts at 'unknown', so the check in
    // hydrate() cannot have fired. Now that the server has answered, a
    // blocked result is worth a second look before anybody acts on it.
    void reconcile()
  }

  /**
   * Double-check a state that would otherwise interrupt the patient, and only
   * then touch the store.
   *
   * The problem it solves: `entitlement_expires_at` only moves when a device
   * presents a receipt, and nothing at launch *looks* at the store for one. So
   * a subscription that renewed with Apple an hour ago is invisible to us
   * until something waits for the plugin to replay it. A paying subscriber
   * would otherwise meet the red "Testversion abgelaufen" bar and have to tap
   * through a paywall they should never have seen.
   *
   * We do not look on every launch. We look only at the moment we would
   * otherwise interrupt somebody — so a patient in their trial, or one the
   * server already calls subscribed, never waits on the store at all:
   *
   *   1. **Ask our own server first.** Free, and it already knows about a
   *      purchase another device verified, a facility licence that was
   *      extended, or an entitlement a device transfer brought in.
   *   2. **Only then wait on the plugin.** `initIap()` attaches `lib/iap.ts`'s
   *      `approved` handler; StoreKit replays every transaction we have not
   *      finished — an auto-renewal among them — and the handler verifies each
   *      against our backend. The verified entitlement arrives back through
   *      `onEntitlementVerified`.
   *
   * This shows nothing, and must never show anything. The policy (CLAUDE.md,
   * "No payment-system UI at launch, ever") is that no payment sheet, Apple
   * Account prompt, Play recovery card or our own paywall appears without the
   * patient tapping into the payment system. `initIap()` is silent by
   * construction: it reaches neither `order()`, nor `restorePurchases()`, nor
   * the receipt refresh (`SKReceiptRefreshRequest`, validator-gated, and we set
   * no `store.validator`). If this function ever needs more than `initIap()`
   * plus waiting, re-read that policy first.
   *
   * One attempt per app run. If it cannot reach the store or the network, the
   * interruption goes ahead: being unable to check is not evidence of
   * entitlement.
   */
  async function reconcile(): Promise<void> {
    if (reconcileInflight !== null) return reconcileInflight
    if (reconcileAttempted) return

    // Nothing to reconcile against. The web build has no store, and the VPP
    // edition is unlocked by its bundle — `effectiveStatus` is pinned there.
    if (!isNative || isVppEdition) return

    // 'unknown' is "we have not heard yet", not "blocked". Reconciling on it
    // would touch the store on every cold launch, which is the whole thing
    // this design avoids.
    if (!isExpired.value) return

    reconcileAttempted = true
    reconciling.value = true

    reconcileInflight = (async () => {
      const before = effectiveStatus.value
      try {
        // Ask our own server — but only if what we are looking at came off
        // disk. When reconcile() was triggered by a refresh that just
        // completed, the server has already answered and asking twice would
        // be a wasted round-trip for the same answer.
        if (usingCache.value) {
          await refreshFromServer()
          if (isUnlocked.value) {
            trackEntitlementReconciled({ outcome: 'server', from: before })
            return
          }
        }

        await initIap()
        await waitForVerification()

        trackEntitlementReconciled({
          outcome: isUnlocked.value ? 'store' : 'still_blocked',
          from: before,
        })
      } catch {
        // No plugin, no network, no store account. Fall through and interrupt.
        trackEntitlementReconciled({ outcome: 'unavailable', from: before })
      } finally {
        reconciling.value = false
        reconcileInflight = null
      }
    })()

    return reconcileInflight
  }

  /**
   * `refresh()` without its tail call back into {@link reconcile} — reconcile
   * is the caller here, and re-entering it would be a loop.
   */
  async function refreshFromServer(): Promise<void> {
    if (refreshInflight !== null) return refreshInflight
    loading.value = true
    refreshInflight = (async () => {
      try {
        const response = await fetchEntitlement()
        applyResponse(response)
        usingCache.value = false
        lastError.value = null
        await saveCachedEntitlement(response)
      } catch (e) {
        lastError.value = e instanceof Error ? e.message : String(e)
      } finally {
        loading.value = false
        refreshInflight = null
      }
    })()
    return refreshInflight
  }

  /**
   * Wait for a replayed transaction to be verified, up to a bounded moment.
   *
   * Nothing to fetch here: `onEntitlementVerified` hands us the entitlement as
   * soon as lib/iap.ts has had it verified, so this waits on that signal and
   * gives up on a timer. The budget covers the plugin's own 300ms debounce plus
   * one verification round-trip; past that we would be making the patient wait
   * to be told something they can act on.
   */
  async function waitForVerification(): Promise<void> {
    // The replay can beat us here: StoreKit delivers to the observer the
    // moment the plugin attaches it, so a renewal may already have been
    // verified and applied before this runs. Nothing left to wait for.
    if (isUnlocked.value) return

    await new Promise<void>((resolve) => {
      const timer = setTimeout(() => {
        verificationSignal = null
        resolve()
      }, RECONCILE_BUDGET_MS)

      verificationSignal = () => {
        clearTimeout(timer)
        verificationSignal = null
        resolve()
      }
    })
  }

  // Every transaction lib/iap.ts verifies lands here — including the renewals
  // StoreKit replays the moment the plugin initializes, which nobody asked for
  // and which would otherwise reach the server without the UI ever hearing
  // about it.
  onEntitlementVerified((response) => {
    void adoptEntitlement(response)
    verificationSignal?.()
  })

  function openPaywall(source: PaywallSource = 'feature_gate'): void {
    // There is nothing to sell on the VPP edition — the license was bought in
    // volume by the institution. Inert rather than absent so no caller has to
    // learn about editions; `isExpired` is permanently false there anyway, so
    // this is belt-and-braces against a future screen calling in directly.
    if (isVppEdition) return
    paywallOpen.value = true
    lastError.value = null
    trackPaywallOpened(source)
  }
  function closePaywall(): void {
    paywallOpen.value = false
  }

  // PurchaseDialog binds its visibility via `v-model:show="paywallOpen"`,
  // so user-driven closes write `paywallOpen = false` directly without
  // going through closePaywall(). A watcher captures every transition,
  // and the purchase/restore success paths flip `suppressClose` first so
  // their programmatic close doesn't double-count against the funnel.
  let suppressClose = false
  watch(paywallOpen, (open, prev) => {
    if (prev && !open) {
      if (suppressClose) {
        suppressClose = false
      } else {
        trackPaywallClosed('user_close')
      }
    }
  })

  // --- Telemetry helpers ----------------------------------------------------

  /**
   * Resolve the SKU-shaped `ProductId` we exchange with the IAP plugin into
   * the schema-friendly shorthand we report to TelemetryDeck. Centralised
   * here so the funnel's `product` dimension stays `lifetime` / `monthly`
   * even if the upstream product SKU is rebranded later.
   */
  function telemetryProduct(productId: ProductId): PurchaseProduct {
    return productId === 'lingotalk_lifetime' ? 'lifetime' : 'monthly'
  }

  function telemetryPlatform(): PurchasePlatform {
    if (isIOS) return 'ios'
    if (isAndroid) return 'android'
    return 'web'
  }

  function telemetryErrorCode(e: unknown): string {
    if (e instanceof IapNotAvailableError) return 'iap_not_available'
    if (e instanceof Error) return e.name === '' ? 'error' : e.name
    return 'unknown'
  }

  function emptyPricing(): PurchasePricing {
    return { priceMicros: null, currency: null }
  }

  /**
   * Resolve the App Store / Play Store-quoted pricing for a product at the
   * moment of purchase. Reads from the loaded catalog; falls back to nulls
   * if the catalog hasn't propagated yet so the funnel still gets the
   * count signal (just without revenue attached for that transaction).
   *
   * Pricing is read at purchase time — not at paywall open — so any
   * promotional repricing or App Store localization change between paywall
   * render and confirm-purchase tap is reflected in the reported number.
   */
  async function pricingFor(productId: ProductId): Promise<PurchasePricing> {
    try {
      const products = await getProducts()
      const found = products.find(
        (p: IapProductInfo) => p.id === productId,
      )
      if (found === undefined) return emptyPricing()
      return {
        priceMicros: found.priceMicros,
        currency: found.currency,
      }
    } catch {
      return emptyPricing()
    }
  }

  /** Fire `trial.expired_detected` once per cached-trial → lapsed flip. */
  let trialExpiredEmitted = false
  function maybeEmitTrialExpired(): void {
    if (trialExpiredEmitted) return
    if (effectiveStatus.value === 'lapsed' && status.value === 'trial') {
      trialExpiredEmitted = true
      trackTrialExpiredDetected()
    }
  }

  /**
   * Start a purchase via the native plugin. The plugin's `approved` listener
   * forwards verification to the backend; we then refresh() to mirror the
   * authoritative server state into the store.
   */
  async function purchase(product: ProductId): Promise<void> {
    // No IAP products exist on the VPP App Store record, so initialising the
    // store plugin here would fail anyway — refuse up front and keep the
    // plugin out of that bundle's runtime entirely.
    if (isVppEdition || !isNative) throw new IapNotAvailableError()
    purchasing.value = true
    lastError.value = null
    // Init first so getProducts() can resolve the store's catalog — without
    // it the priceMicros/currency on the telemetry signals would always be
    // null, and the funnel would be unable to report revenue. If the store
    // catalog hasn't propagated yet (rare on a slow network), pricing falls
    // back to nulls and the count signal still lands.
    try {
      await initIap()
    } catch (e) {
      trackPurchaseFailed({
        product: telemetryProduct(product),
        platform: telemetryPlatform(),
        pricing: emptyPricing(),
        errorCode: telemetryErrorCode(e),
      })
      lastError.value = e instanceof Error ? e.message : String(e)
      purchasing.value = false
      throw e
    }
    const pricing = await pricingFor(product)
    const telemetryArgs = {
      product: telemetryProduct(product),
      platform: telemetryPlatform(),
      pricing,
    }
    trackPurchaseStarted(telemetryArgs)
    try {
      await iapPurchase(product)
      await refresh()
      trackPurchaseCompleted(telemetryArgs)
      suppressClose = true
      paywallOpen.value = false
    } catch (e) {
      if (e instanceof IapPurchaseCancelledError) {
        // User-cancelled; not an error worth surfacing as a banner. The
        // dialog stays open so they can pick a different option or close.
        // Not a `purchase.failed` either — distinguishing cancels from real
        // errors in the funnel matters.
        return
      }
      trackPurchaseFailed({
        ...telemetryArgs,
        errorCode: telemetryErrorCode(e),
      })
      lastError.value = e instanceof Error ? e.message : String(e)
      throw e
    } finally {
      purchasing.value = false
    }
  }

  /** Restore previously-bought items for the current store account. */
  async function restore(): Promise<void> {
    if (isVppEdition || !isNative) throw new IapNotAvailableError()
    purchasing.value = true
    lastError.value = null
    trackRestoreStarted()
    try {
      await initIap()
      await iapRestore()
      // Plugin replays approved transactions, listener calls /verify per tx;
      // by the time restore() resolves the patient row should be updated.
      await refresh()
      // We don't have a count of restored transactions from the plugin
      // surface — report 1 when the restore unlocked entitlement and 0
      // when it didn't (nothing to restore on this Apple/Google account).
      trackRestoreCompleted(isUnlocked.value ? 1 : 0)
      if (isUnlocked.value) {
        suppressClose = true
        paywallOpen.value = false
      }
    } catch (e) {
      trackRestoreFailed(telemetryErrorCode(e))
      lastError.value = e instanceof Error ? e.message : String(e)
      throw e
    } finally {
      purchasing.value = false
    }
  }

  /**
   * Test-only — drops the on-disk cache and resets in-memory state to
   * the same shape as a fresh install. Not used on the happy path.
   */
  async function _reset(): Promise<void> {
    // Release a reconcile that is still waiting on its timer; otherwise it
    // would resolve against a store that has since been reset.
    verificationSignal?.()
    reconcileAttempted = false
    reconcileInflight = null
    reconciling.value = false
    await clearCachedEntitlement()
    status.value = 'unknown'
    trialDaysRemaining.value = null
    trialEndsAt.value = null
    expiresAt.value = null
    productId.value = null
    platform.value = null
    organizationName.value = null
    usingCache.value = false
    lastError.value = null
  }

  return {
    // state
    status,
    trialDaysRemaining,
    trialEndsAt,
    expiresAt,
    productId,
    platform,
    organizationName,
    loading,
    purchasing,
    lastError,
    paywallOpen,
    usingCache,
    // getters
    effectiveStatus,
    isUnlocked,
    isOrgLicensed,
    isTrial,
    isExpired,
    // actions
    hydrate,
    refresh,
    reconcile,
    redeemOrgToken,
    adoptEntitlement,
    openPaywall,
    closePaywall,
    purchase,
    restore,
    _reset,
  }
})
